/**
 * Set Dressing Refs Store
 *
 * Per-project, per-user set dressing reference image bank.
 * Caches non-hidden bank refs keyed by projectId.
 */

import { create } from 'zustand';
import { listSetDressingBank } from '../services/visionAssetsClient.js';

export const useSetDressingRefsStore = create((set, get) => ({
  refsByProject: {},
  loadingProjects: new Set(),

  /**
   * Load set dressing refs for a project (if not already cached).
   */
  loadSetDressingRefs: async (projectId) => {
    const state = get();
    if (state.refsByProject[projectId]) return state.refsByProject[projectId];
    if (state.loadingProjects.has(projectId)) {
      // Wait for in-flight request
      return new Promise((resolve) => {
        const unsub = useSetDressingRefsStore.subscribe((s) => {
          if (s.refsByProject[projectId]) {
            unsub();
            resolve(s.refsByProject[projectId]);
          }
        });
      });
    }

    set((s) => ({ loadingProjects: new Set([...s.loadingProjects, projectId]) }));
    try {
      const refs = await listSetDressingBank(projectId);
      set((s) => ({
        refsByProject: { ...s.refsByProject, [projectId]: refs || [] },
        loadingProjects: new Set([...s.loadingProjects].filter(id => id !== projectId)),
      }));
      return refs || [];
    } catch (error) {
      console.error('[SetDressingRefsStore] Load error:', error);
      set((s) => ({
        loadingProjects: new Set([...s.loadingProjects].filter(id => id !== projectId)),
      }));
      return [];
    }
  },

  /**
   * Add a new ref image to the cache (after upload).
   */
  addRef: (projectId, ref) => {
    set((s) => ({
      refsByProject: {
        ...s.refsByProject,
        [projectId]: [ref, ...(s.refsByProject[projectId] || [])],
      },
    }));
  },

  /**
   * Remove a ref image from the cache (after delete).
   */
  removeRef: (projectId, refId) => {
    set((s) => ({
      refsByProject: {
        ...s.refsByProject,
        [projectId]: (s.refsByProject[projectId] || []).filter(r => r.id !== refId),
      },
    }));
  },

  /**
   * Move a ref image to the front (recently used).
   */
  bumpRef: (projectId, refId) => {
    set((s) => {
      const list = s.refsByProject[projectId] || [];
      const ref = list.find(r => r.id === refId);
      if (!ref) return s;
      return {
        refsByProject: {
          ...s.refsByProject,
          [projectId]: [ref, ...list.filter(r => r.id !== refId)],
        },
      };
    });
  },

  /**
   * Invalidate cache for a project (force re-fetch on next load).
   */
  invalidate: (projectId) => {
    set((s) => {
      const next = { ...s.refsByProject };
      delete next[projectId];
      return { refsByProject: next };
    });
  },

  /**
   * Clear all caches (call on logout).
   */
  reset: () => {
    set({ refsByProject: {}, loadingProjects: new Set() });
  },
}));
